import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useIdentity } from './AuthProvider';
import { SignInFailed, SigningIn } from './screens';

/**
 * Where Okta sends the browser back (CHG-050). A refusal arrives as `error` and
 * `error_description` in the URL (for example `access_denied`, "User is not assigned to the
 * client application."), and is shown as it came, with a button: nothing signs in again by itself,
 * or a refusal would loop.
 */
export function LoginCallback() {
  const { enabled, completeSignIn, signIn } = useIdentity();
  const { search } = useLocation();
  const navigate = useNavigate();
  const [failure, setFailure] = useState<string | null>(null);
  // StrictMode runs the effect twice, and an authorization code is good once.
  const started = useRef(false);

  const params = new URLSearchParams(search);
  const error = params.get('error');
  const refusal = error
    ? params.get('error_description')?.trim() || error
    : params.has('code')
      ? null
      : 'Sign-in did not finish.';

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!enabled) {
      void navigate('/', { replace: true });
      return;
    }
    if (refusal) return;
    completeSignIn().then(
      (path) => void navigate(path, { replace: true }),
      (cause: unknown) =>
        setFailure(cause instanceof Error ? cause.message : 'Sign-in did not finish.'),
    );
  }, [enabled, refusal, completeSignIn, navigate]);

  const message = refusal ?? failure;
  if (!enabled || !message) return <SigningIn />;
  return <SignInFailed message={message} onSignIn={() => signIn('/', true)} />;
}
